import type {
  CascaderOption,
  CascaderSubmenu,
  CascaderOptionWithPath,
} from './types';

export const isSubmenu = <OptionType extends CascaderOption>(
  item: OptionType,
): item is CascaderSubmenu<OptionType> => {
  return (
    'children' in item &&
    Array.isArray(item.children) &&
    item.children.length > 0
  );
};

// Convert a value path to an index path
export const toIndexPath = <OptionType extends CascaderOption>(
  valuePath: string[],
  options: OptionType[],
): number[] => {
  const indexPath: number[] = [];
  let currentOptions = options;

  for (const value of valuePath) {
    const needle = String(value);
    const index = currentOptions.findIndex(
      (opt) => String(opt.value) === needle,
    );
    if (index === -1) return [];
    indexPath.push(index);

    const selectedOption = currentOptions[index];
    if (isSubmenu(selectedOption)) {
      currentOptions = selectedOption.children.flatMap(
        (group) => group.children,
      );
    } else {
      break;
    }
  }

  return indexPath;
};

export type LeafOptionAtValuePathResult<OptionType extends CascaderOption> = {
  option: OptionType;
  /** Path segments that actually reach the leaf (may be shorter than the input when {@link toIndexPath} stops at an early leaf). */
  path: string[];
};

/**
 * Resolves the leaf at the end of a value path the same way menu navigation does
 * (walk the tree from root options using {@link toIndexPath}). Returns a leaf only — not a submenu row.
 *
 * Input paths may be longer than the navigable depth: {@link toIndexPath} stops when it hits a leaf,
 * so we use the effective prefix path for selection (matches clicking a 3-deep leaf even if the caller
 * passed an extra supplier segment).
 */
export const getLeafOptionAtValuePath = <OptionType extends CascaderOption>(
  valuePath: string[],
  rootOptions: OptionType[],
): LeafOptionAtValuePathResult<OptionType> | undefined => {
  const normalizedPath = valuePath.map(String);
  const indexPath = toIndexPath(normalizedPath, rootOptions);
  if (indexPath.length === 0) {
    return undefined;
  }

  const effectivePath = normalizedPath.slice(0, indexPath.length);

  let currentOptions = rootOptions;
  let opt: OptionType | undefined;
  for (let i = 0; i < indexPath.length; i++) {
    const idx = indexPath[i];
    opt = currentOptions[idx];
    if (!opt) {
      return undefined;
    }
    if (i === indexPath.length - 1) {
      if (isSubmenu(opt)) {
        return undefined;
      }
      return { option: opt, path: effectivePath };
    }
    if (!isSubmenu(opt)) {
      return undefined;
    }
    currentOptions = opt.children.flatMap((group) => group.children);
  }
  return undefined;
};

/**
 * Flattens cascader options into a single array for searching and path finding
 * @param options The cascader options to flatten
 * @returns A flat array of all options
 */
export function flattenOptions<OptionType extends CascaderOption>(
  options: OptionType[],
): OptionType[] {
  const result: OptionType[] = [];

  for (const opt of options) {
    if (isSubmenu<OptionType>(opt)) {
      result.push(opt);
      for (const group of opt.children) {
        result.push(...flattenOptions<OptionType>(group.children));
      }
    } else {
      result.push(opt);
    }
  }
  return result;
}
// Add fullPath and fullLabels to options recursively
export const addPathsToOptions = <OptionType extends CascaderOption>(
  options: OptionType[],
  parentPath: string[] = [],
  parentLabels: string[] = [],
): CascaderOptionWithPath<OptionType>[] => {
  const result: CascaderOptionWithPath<OptionType>[] = [];

  for (const opt of options) {
    const fullPath = [...parentPath, opt.value];
    const fullLabels = [...parentLabels, opt.label];

    if (isSubmenu<OptionType>(opt)) {
      // This is a submenu, add paths to children recursively
      const submenuWithPath = {
        ...opt,
        fullPath,
        fullLabels,
        children: opt.children.map((group) => ({
          ...group,
          children: addPathsToOptions(group.children, fullPath, fullLabels),
        })),
      };
      result.push(submenuWithPath);
    } else {
      // This is a leaf option
      const leafWithPath = {
        ...opt,
        fullPath,
        fullLabels,
      };
      result.push(leafWithPath);
    }
  }

  return result;
};

/**
 * Traverses the cascader options tree to get options at a specific level
 * based on the cursor path.
 *
 * @param rootOptions - The root level options
 * @param cursorPath - Array of indices representing the current selection path
 * @param targetLevel - The level to get options for (0 for root level)
 * @returns Array of options at the specified level
 */
export const getOptionsAtLevel = <OptionType extends CascaderOption>(
  rootOptions: OptionType[],
  cursorPath: number[],
  targetLevel: number,
): OptionType[] => {
  if (targetLevel === 0) return rootOptions;

  let currentOpts = rootOptions;
  for (let i = 0; i < targetLevel; i++) {
    const currentIndex = cursorPath[i];
    if (currentIndex >= 0 && currentIndex < currentOpts.length) {
      const currentOption = currentOpts[currentIndex];
      if (isSubmenu(currentOption)) {
        currentOpts = currentOption.children.flatMap((group) => group.children);
      } else {
        return [];
      }
    } else {
      return [];
    }
  }
  return currentOpts;
};
